import { useEffect, useState, useCallback } from 'react'
import { apiAdminGetRounds, apiAdminGetGames, ApiRequestError } from '@/lib/api'
import type { Round, Game, PaginatedResult } from '@nexbit/types'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import type { Dict } from '@/lib/i18n'

function roundStatusVariant(
  status: string,
): 'success' | 'destructive' | 'warning' | 'outline' {
  switch (status) {
    case 'open':     return 'success'
    case 'settled':  return 'outline'
    case 'settling': return 'warning'
    case 'closed':   return 'outline'
    case 'cancelled':return 'destructive'
    default:         return 'outline'
  }
}

const STATUSES = ['', 'open', 'closed', 'settling', 'settled', 'cancelled'] as const

function statusLabel(t: Dict, s: string): string {
  const map: Record<string, string> = {
    open: t.rounds.statusOpen, closed: t.rounds.statusClosed, settling: t.rounds.statusSettling,
    settled: t.rounds.statusSettled, cancelled: t.rounds.statusCancelled,
  }
  return map[s] ?? s
}

export default function AdminRoundsPage() {
  const t = useT()
  const [data, setData] = useState<PaginatedResult<Round> | null>(null)
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [gameId, setGameId] = useState('')
  const [status, setStatus] = useState('')

  const load = useCallback(async (p: number, gid: string, s: string) => {
    setLoading(true)
    setError(null)
    try {
      setData(
        await apiAdminGetRounds({
          page: p,
          limit: 20,
          gameId: gid || undefined,
          status: s || undefined,
        }),
      )
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    apiAdminGetGames().then(setGames).catch(() => undefined)
  }, [])

  useEffect(() => { void load(page, gameId, status) }, [load, page, gameId, status])

  function handleFilter() {
    setPage(1)
    void load(1, gameId, status)
  }

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  const selectClass = cn(
    'h-8 rounded-[var(--radius-sm)] border border-input bg-transparent px-2',
    'font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
  )

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">{t.rounds.title}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t.rounds.subtitle}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={gameId}
          onChange={(e) => { setGameId(e.target.value); setPage(1) }}
          className={selectClass}
          aria-label={t.rounds.filterGame}
        >
          <option value="">{t.rounds.allGames}</option>
          {games.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1) }}
          className={selectClass}
          aria-label={t.rounds.filterStatus}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s ? statusLabel(t, s) : t.rounds.allStatus}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={handleFilter} className="h-8 text-xs">
          {t.common.filter}
        </Button>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.rounds.history}
          </span>
          {data && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">
              {data.total} {t.rounds.unit}
            </span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-5 w-16" />
                <Skeleton className="ml-auto h-4 w-24" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">{t.rounds.noRounds}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>{t.rounds.game}</TableHead>
                <TableHead>{t.rounds.status}</TableHead>
                <TableHead>{t.rounds.result}</TableHead>
                <TableHead className="text-right">{t.rounds.openAt}</TableHead>
                <TableHead className="text-right">{t.rounds.closeAt}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.roundNumber}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-muted-foreground">
                      {r.gameId.slice(-6)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={roundStatusVariant(r.status)} className="text-[10px]">
                      {statusLabel(t, r.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {r.result ? (
                      <span className="font-mono text-xs">
                        {r.result.sum}{' '}
                        <span className={r.result.highLow === 'high' ? 'text-success' : 'text-destructive'}>
                          {r.result.highLow === 'high' ? t.rounds.high : t.rounds.low}
                        </span>
                        {' / '}
                        <span className="text-muted-foreground">{r.result.oddEven === 'odd' ? t.rounds.odd : t.rounds.even}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/40 font-mono text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <time className="font-mono text-xs text-muted-foreground">
                      {format(new Date(r.openAt), 'dd/MM HH:mm:ss')}
                    </time>
                  </TableCell>
                  <TableCell className="text-right">
                    <time className="font-mono text-xs text-muted-foreground">
                      {format(new Date(r.closeAt), 'dd/MM HH:mm:ss')}
                    </time>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
            <span className="font-mono text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="หน้าก่อน"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="หน้าถัดไป"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
